import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Package, AlertTriangle, ArrowUpCircle, ArrowDownCircle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { BarChart } from "@tremor/react";
import { cn } from "@/lib/utils";

interface DashboardData {
  totalProducts: number;
  lowStockItems: number;
  itemsReceived: number;
  itemsIssued: number;
}

interface Movement {
  id: string;
  product_name: string;
  quantity: number;
  type: 'entrada' | 'saida';
  created_at: string;
}

interface MonthlyMovement {
  month: string;
  total: number;
}

export default function Inventory() {
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    totalProducts: 0,
    lowStockItems: 0,
    itemsReceived: 0,
    itemsIssued: 0
  });
  const [recentMovements, setRecentMovements] = useState<Movement[]>([]);
  const [monthlyData, setMonthlyData] = useState<MonthlyMovement[]>([]);

  useEffect(() => {
    loadDashboardData();
    loadRecentMovements();
    loadMonthlyData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const { data: totalProducts, error: totalError } = await supabase
        .from('products')
        .select('id', { count: 'exact' });

      const { data: lowStock, error: lowStockError } = await supabase
        .from('products')
        .select('id')
        .lt('quantity', 10);

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { data: received, error: receivedError } = await supabase
        .from('inventory_movements')
        .select('id')
        .eq('type', 'entrada')
        .gte('created_at', thirtyDaysAgo.toISOString());

      const { data: issued, error: issuedError } = await supabase
        .from('inventory_movements')
        .select('id')
        .eq('type', 'saida')
        .gte('created_at', thirtyDaysAgo.toISOString());

      if (totalError || lowStockError || receivedError || issuedError) {
        throw new Error('Erro ao carregar dados do dashboard');
      }

      setDashboardData({
        totalProducts: totalProducts?.length || 0,
        lowStockItems: lowStock?.length || 0,
        itemsReceived: received?.length || 0,
        itemsIssued: issued?.length || 0
      });
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
    }
  };

  const loadRecentMovements = async () => {
    try {
      const { data, error } = await supabase
        .from('inventory_movements')
        .select(`
          id,
          product_name:products(name),
          quantity,
          type,
          created_at
        `)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setRecentMovements(data || []);
    } catch (error) {
      console.error('Erro ao carregar movimentações recentes:', error);
    }
  };

  const loadMonthlyData = async () => {
    try {
      // Últimos 6 meses
      const months = Array.from({ length: 6 }, (_, i) => {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        return date.toISOString().slice(0, 7); // YYYY-MM
      }).reverse();

      const monthlyMovements = await Promise.all(
        months.map(async (month) => {
          const startDate = `${month}-01`;
          const endDate = new Date(month.slice(0, 4), parseInt(month.slice(5, 7)), 0)
            .toISOString()
            .slice(0, 10);

          const { data, error } = await supabase
            .from('inventory_movements')
            .select('quantity')
            .gte('created_at', startDate)
            .lte('created_at', endDate);

          if (error) throw error;

          return {
            month: new Date(month + '-01').toLocaleString('pt-BR', { month: 'short' }),
            total: data?.reduce((sum, item) => sum + item.quantity, 0) || 0
          };
        })
      );

      setMonthlyData(monthlyMovements);
    } catch (error) {
      console.error('Erro ao carregar dados mensais:', error);
    }
  };

  return (
    <div className="flex-1 space-y-4 p-4 md:p-8 pt-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Dashboard</h1>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Produtos</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData.totalProducts}</div>
            <p className="text-xs text-muted-foreground">Produtos cadastrados</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Estoque Baixo</CardTitle>
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData.lowStockItems}</div>
            <p className="text-xs text-muted-foreground">Produtos com menos de 10 unidades</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Entradas (30d)</CardTitle>
            <ArrowUpCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData.itemsReceived}</div>
            <p className="text-xs text-muted-foreground">Produtos recebidos nos últimos 30 dias</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Saídas (30d)</CardTitle>
            <ArrowDownCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboardData.itemsIssued}</div>
            <p className="text-xs text-muted-foreground">Produtos retirados nos últimos 30 dias</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Tendências de Movimentação</CardTitle>
          </CardHeader>
          <CardContent>
            <BarChart
              data={monthlyData}
              index="month"
              categories={["total"]}
              colors={["blue"]}
              valueFormatter={(value) => value.toString()}
              yAxisWidth={48}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Movimentações Recentes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentMovements.map((movement) => (
                <div
                  key={movement.id}
                  className="flex items-center justify-between border-b pb-2 last:border-0"
                >
                  <div>
                    <p className="font-medium">{movement.product_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(movement.created_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <div className={cn(
                    "font-medium",
                    movement.type === 'entrada' ? "text-green-500" : "text-red-500"
                  )}>
                    {movement.type === 'entrada' ? '+' : '-'}{movement.quantity}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
